<script setup lang="ts">
import { ChevronRight, RefreshCw } from 'lucide-vue-next'
import DeckPlayerHost from '../../deckplayer/components/DeckPlayerHost.vue'
import { useFlowEditorContext } from '../composables/useFlowEditor'

/** Step/play controls, live engine state and the embedded deck player. */
const editor = useFlowEditorContext()
const { rightRailCollapsed, visualDisableShadow } = editor.ui
const { wpCtx, previewRestRoot, props } = editor
const expandedPreviewFlowGraph = editor.previewGraph
const {
  previewHostRef,
  previewSessionKey,
  previewPlaying,
  previewPaused,
  previewNeedsUserInteraction,
  previewActiveCursorNodeId,
  previewSessionStatus,
  previewStepSignal,
  debugPlayDelay,
  previewRegisters,
  previewPhaseId,
  previewGroupId,
  previewDebugState,
  handlePreviewStepChanged,
  stepPreview,
  restartPreview,
  pausePreview,
  continuePreview,
  clearBackendSession,
} = editor.preview
</script>

<template>
  <section class="panel flow-debug-panel">
    <header class="panel__header flow-rail__header">
      <h2>{{ $t('flow.preview') }}</h2>
      <button
        type="button"
        class="panel-collapse-btn panel-collapse-btn--side"
        :title="$t('flow.collapsePanel')"
        @click="rightRailCollapsed = true"
      >
        <ChevronRight style="width: 0.9rem; height: 0.9rem" aria-hidden="true" />
      </button>
    </header>
    <div class="panel__body flow-debug-panel__body">
      <!-- Playback controls -->
      <div class="preview-controls">
        <!-- Playing -->
        <template v-if="previewPlaying">
          <button
            type="button"
            class="pl-btn pl-btn--secondary"
            :title="$t('flow.previewPauseTitle')"
            @click="pausePreview"
          >
            ⏸ {{ $t('flow.previewPause') }}
          </button>
          <button
            type="button"
            class="pl-btn pl-btn--secondary"
            :title="$t('flow.previewStopTitle')"
            @click="restartPreview(false)"
          >
            ■ {{ $t('flow.previewStop') }}
          </button>
        </template>
        <!-- Paused (was playing, state preserved) -->
        <template v-else-if="previewPaused">
          <button
            type="button"
            class="pl-btn pl-btn--primary"
            :disabled="previewNeedsUserInteraction || !previewActiveCursorNodeId"
            :title="
              previewNeedsUserInteraction
                ? $t('flow.previewClickCardFirst')
                : $t('flow.previewAdvanceOneStep')
            "
            @click="stepPreview"
          >
            ▶ {{ $t('flow.previewStep') }}
          </button>
          <button
            type="button"
            class="pl-btn pl-btn--secondary"
            :title="$t('flow.previewContinueTitle')"
            @click="continuePreview"
          >
            ▶▶ {{ $t('flow.previewContinue') }}
          </button>
          <button
            type="button"
            class="pl-btn pl-btn--secondary"
            :title="$t('flow.previewStopTitle')"
            @click="restartPreview(false)"
          >
            ■ {{ $t('flow.previewStop') }}
          </button>
        </template>
        <!-- Idle -->
        <template v-else>
          <button
            type="button"
            class="pl-btn pl-btn--primary"
            :disabled="previewNeedsUserInteraction || !previewActiveCursorNodeId"
            :title="
              previewNeedsUserInteraction
                ? $t('flow.previewClickCardFirst')
                : $t('flow.previewAdvanceOneStep')
            "
            @click="stepPreview"
          >
            ▶ {{ $t('flow.previewStep') }}
          </button>
          <button
            type="button"
            class="pl-btn pl-btn--secondary"
            :title="$t('flow.previewPlayTitle')"
            @click="restartPreview(true)"
          >
            ▶▶ {{ $t('flow.previewPlay') }}
          </button>
        </template>
        <button
          type="button"
          class="pl-btn pl-btn--secondary"
          :title="$t('flow.previewReloadTitle')"
          @click="restartPreview(previewPlaying)"
        >
          <RefreshCw style="width: 0.9rem; height: 0.9rem" aria-hidden="true" />
        </button>
        <button
          v-if="wpCtx"
          type="button"
          class="pl-btn pl-btn--secondary"
          :title="$t('flow.previewClearSessionTitle')"
          @click="clearBackendSession"
        >
          ✕
        </button>
      </div>
      <div v-if="previewSessionStatus" class="preview-session__status">
        {{ previewSessionStatus }}
      </div>

      <!-- Play speed slider (only relevant in Play mode) -->
      <div class="debug-speed-row">
        <label class="debug-speed-label">
          <span>{{ $t('flow.playDelay') }}: {{ debugPlayDelay }}ms</span>
          <input
            type="range"
            min="25"
            max="2000"
            step="25"
            :value="debugPlayDelay"
            class="debug-speed-slider"
            @input="debugPlayDelay = Number(($event.target as HTMLInputElement).value)"
          />
        </label>
      </div>

      <div v-if="previewNeedsUserInteraction" class="preview-hint--interaction">
        ↓ {{ $t('flow.clickCardToInteract') }}
      </div>

      <!-- Live state -->
      <div class="preview-state">
        <div class="preview-state__row">
          <span class="preview-state__key">{{ $t('flow.stateNode') }}</span>
          <span class="preview-state__val">{{ previewActiveCursorNodeId ?? '—' }}</span>
        </div>
        <div v-if="previewPhaseId" class="preview-state__row">
          <span class="preview-state__key">{{ $t('flow.statePhase') }}</span>
          <span class="preview-state__val">{{ previewPhaseId }}</span>
        </div>
        <div v-if="previewGroupId" class="preview-state__row">
          <span class="preview-state__key">{{ $t('flow.stateGroup') }}</span>
          <span class="preview-state__val">{{ previewGroupId }}</span>
        </div>

        <!-- Registers -->
        <div class="preview-state__section-label">{{ $t('flow.stateRegisters') }}</div>
        <div class="preview-state__registers">
          <span v-for="(val, key) in previewRegisters" :key="key" class="preview-state__reg">
            {{ key }}: {{ val }}
          </span>
        </div>

        <!-- Getter sources (all live values) -->
        <template v-if="previewDebugState">
          <div class="preview-state__section-label">{{ $t('flow.stateGetters') }}</div>
          <div class="preview-state__row">
            <span class="preview-state__key">deck-size</span>
            <span class="preview-state__val">{{ previewDebugState.deckSize }}</span>
          </div>
          <div class="preview-state__row">
            <span class="preview-state__key">deck-pool-remaining</span>
            <span class="preview-state__val">{{ previewDebugState.deckPoolRemaining }}</span>
          </div>
          <div class="preview-state__row">
            <span class="preview-state__key">deck-done</span>
            <span class="preview-state__val">{{ previewDebugState.deckDone }}</span>
          </div>
          <div class="preview-state__row">
            <span class="preview-state__key">group-size</span>
            <span class="preview-state__val">{{
              previewGroupId ? previewDebugState.groupSize : '—'
            }}</span>
          </div>
          <div class="preview-state__row">
            <span class="preview-state__key">group-pool-remaining</span>
            <span class="preview-state__val">{{
              previewGroupId ? previewDebugState.groupPoolRemaining : '—'
            }}</span>
          </div>
          <div class="preview-state__row">
            <span class="preview-state__key">group-done</span>
            <span class="preview-state__val">{{
              previewGroupId ? previewDebugState.groupDone : '—'
            }}</span>
          </div>

          <!-- Setter targets -->
          <div class="preview-state__section-label">{{ $t('flow.stateSetters') }}</div>
          <div class="preview-state__row">
            <span class="preview-state__key">progress-total</span>
            <span class="preview-state__val">{{
              previewDebugState.progressTotal ?? '(auto)'
            }}</span>
          </div>
          <div class="preview-state__row">
            <span class="preview-state__key">progress-current</span>
            <span class="preview-state__val">{{
              previewDebugState.progressCurrent ?? '(auto)'
            }}</span>
          </div>
        </template>
      </div>

      <!-- Embedded player -->
      <div v-if="wpCtx" class="preview-player">
        <DeckPlayerHost
          ref="previewHostRef"
          :key="previewSessionKey"
          :deck-id="String(wpCtx.postId)"
          model="flowchart"
          :rest-root="previewRestRoot"
          :nonce="wpCtx.nonce"
          :override-flow-graph="expandedPreviewFlowGraph"
          :manual-step="!previewPlaying"
          :step-signal="previewStepSignal"
          :debug-step-delay="previewPlaying ? debugPlayDelay : 0"
          :in-editor="true"
          :keep-card-on-flow-end="props.nodeMode === 'macro'"
          :passthrough-props="{
            scale: 1,
            showProgressBar: true,
            showCount: true,
            disableAutoplay: true,
            shadow: visualDisableShadow ? 'none' : undefined,
            showPoints: false,
            showPearls: false,
          }"
          @flowchart-step-changed="handlePreviewStepChanged"
        />
      </div>
      <div v-else class="preview-warn preview-warn--block">
        {{ $t('flow.previewRequiresWordPress') }}
      </div>
    </div>
  </section>
</template>

<style scoped src="./styles/panelCollapse.css"></style>

<style scoped>
.flow-debug-panel {
  flex: 0 0 auto;
}

.flow-debug-panel__body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
  padding: 0.5rem;
}

.flow-rail__header {
  display: flex;
  align-items: center;
  min-width: 0;
}

.preview-controls {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}

.preview-hint--interaction {
  font-size: 0.78rem;
  color: #b45309;
  background: #fef9c3;
  border: 1px solid #fcd34d;
  border-radius: 0.4rem;
  padding: 0.35rem 0.5rem;
  text-align: center;
}

.preview-warn {
  font-size: 0.8rem;
  color: #b45309;
}

.preview-warn--block {
  background: #fef9c3;
  border: 1px solid #fcd34d;
  border-radius: 0.5rem;
  padding: 0.5rem 0.6rem;
}

.preview-player {
  flex: 1;
  min-height: 0;
  overflow: visible;
  padding: 12px;
}

/* ── Debug speed slider ──────────────────────────────────────── */
.debug-speed-row {
  padding: 0.1rem 0;
}

.debug-speed-label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.76rem;
  color: #475569;
  font-weight: 500;
}

.debug-speed-slider {
  width: 100%;
  accent-color: var(--pl-color-primary, #4338ca);
}

/* ── Preview state section labels ────────────────────────────── */
.preview-state__section-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-top: 0.3rem;
}

/* ── Preview state debug panel ───────────────────────────────── */
.preview-state {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.5rem 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.5rem;
}

.preview-state__row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.78rem;
}

.preview-state__key {
  color: #64748b;
  font-weight: 600;
  flex: 0 0 auto;
}

.preview-state__val {
  color: #0f172a;
  font-family: monospace;
  font-size: 0.76rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
}

.preview-state__registers {
  display: flex;
  gap: 0.4rem;
  margin-top: 0.15rem;
  flex-wrap: wrap;
}

.preview-state__reg {
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 0.35rem;
  padding: 0.15rem 0.45rem;
  font-family: monospace;
  font-size: 0.76rem;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Preview session debug buttons ───────────────────────────── */
.preview-session__status {
  font-size: 0.75rem;
  color: #64748b;
  font-style: italic;
}
</style>
